import React, {Suspense, useCallback, useEffect, useMemo, useState} from "react"

import dayjs from "dayjs"
import PropTypes from "prop-types"
import {connect} from "react-redux"
import {Route, Routes} from "react-router-dom"

import {getParameter} from "../actions/parameters/parametersAction"
import ContactBanner from "../components/ContactBanner"
import {AREA_TYPE} from "../constants/areaType"
import {menuContent} from "../constants/menu/menu"
import {Page} from "../constants/page/pages"
import {PagesArchitecture} from "../constants/page/pagesArchitecture"
import {defaultParameters} from "../constants/parameters/parameters"
import LOGO from "../img/logo.png"
import {saveCookiesChoices} from "../shared-components/actions/localStorage/cookies/cookiesActions"
import {setResponsive} from "../shared-components/actions/temporaries/menu/menuAction"
import {disconnectUser, logoutUser} from "../shared-components/common/actions/user/userActions"
import CheckBrowserContainer from "../shared-components/components/CheckBrowser/checkBrowserContainer"
import CookiesModal from "../shared-components/components/CookiesModal"
import Footer from "../shared-components/components/Footer/footer"
import MenuContainer from "../shared-components/components/Menu/menu"
import MenuTop from "../shared-components/components/MenuTop/menuTop"
import {DOMISERVE_DONNEES_PERSONNELLES_URL} from "../shared-components/constants/links"
import {useRouter} from "../shared-components/hooks/useRouter"
import withRouter from "../shared-components/wrappers/withRouter"
import {showCookiesModal} from "../store/selectors/localStorage/cookiesSelectors"
import {getDynamicConfigurationValue} from "../store/selectors/localStorage/dynamicConfigurationSelectors"
import {getStyle} from "../store/selectors/localStorage/styleSelectors"
import {getTexts} from "../store/selectors/localStorage/textSelector"
import {
    getExpirationDate,
    getUserFullname,
    getUserInfo,
    getUserPermissions,
    isUserConnected,
} from "../store/selectors/sessionStorage/userSelectors"
import {isMenuResponsive} from "../store/selectors/temporaries/menuSelectors"
import {getActualPage} from "../store/selectors/temporaries/pageSelectors"
import {updateGlobalTexts} from "../styles/globalText"
import prestadomStyle from "../styles/prestadomStyle"
import {AsyncBreadcrumbLayout} from "./asyncScenes"
import {MatomoTrackPageView} from "./matomoTrackPageView"

import "./mainLayout.scss"

const MainLayout = ({
    actualPage,
    disconnectUser,
    features,
    getParam,
    isCookiesModalOpened,
    isMenuResponsive,
    isUserConnected,
    logoutUser,
    onConnectClick,
    onCookiesClick,
    onCookiesClose,
    onHomeClick,
    onLegalClick,
    onProfileClick,
    onRgpdClick,
    onVisualisationChoiceClick,
    setMenuResponsive,
    userInfo,
    userPermissions,
    username,
}) => {
    const [eAttestationsDeclarantUrl, setEAttestationsDeclarantUrl] = useState("")
    const [contactLink, setContactLink] = useState(null)
    const {navigate} = useRouter()

    const fullContactLink = useMemo(
        // On pré rempli le numéro du prestataire dans le formulaire de contact Zendesk
        () => (contactLink && userInfo?.number ? `${contactLink}&tf_26990329345042=${userInfo.number}` : undefined),
        [contactLink, userInfo?.number],
    )

    useEffect(() => {
        getParam(defaultParameters.eAttestationsDeclarantUrl).then(payload =>
            setEAttestationsDeclarantUrl(payload.valeur),
        )
        getParam(defaultParameters.contactLink).then(payload => setContactLink(payload.valeur))
    }, [getParam])

    const getBreadcrumbLayout = useCallback(
        () => <AsyncBreadcrumbLayout actualPage={actualPage} isUserConnected={isUserConnected} />,
        [actualPage, isUserConnected],
    )

    const onDisconnectClick = useCallback(() => {
        logoutUser()
        disconnectUser()
    }, [logoutUser, disconnectUser])

    const defaultRouteExternalSelectors = {
        getStyle,
        getTexts,
        getDynamicConfigurationValue,
        getExpirationDate,
        updateGlobalTexts,
        customStyle: prestadomStyle,
    }

    return (
        <div
            className={
                "main-layout" +
                (process.env.NODE_ENV === "production" && !process.env.REACT_APP_PROD_MODE
                    ? " main-layout-bordered"
                    : "")
            }
        >
            <MatomoTrackPageView />
            <MenuTop
                areaType={AREA_TYPE}
                canAccessUserProfile={true}
                isConnected={isUserConnected}
                onConnectClick={onConnectClick}
                onDisconnectClick={onDisconnectClick}
                onProfileClick={onProfileClick}
                onVisualisationChoiceClick={onVisualisationChoiceClick}
                username={username}
            />
            <div className={(isMenuResponsive ? "main-page-responsive" : "main-page-not-responsive") + " main-page"}>
                <div className="menu-container">
                    <MenuContainer
                        areaType={AREA_TYPE}
                        features={features}
                        isConnected={isUserConnected}
                        isMenuResponsive={isMenuResponsive}
                        menuContent={menuContent(actualPage, navigate, features, eAttestationsDeclarantUrl)}
                        onConnectClick={onConnectClick}
                        onDisconnectClick={onDisconnectClick}
                        onHomeClick={onHomeClick}
                        onProfileClick={onProfileClick}
                        setMenuResponsive={setMenuResponsive}
                        staticLogo={LOGO}
                        title={AREA_TYPE}
                        userPermissions={userPermissions}
                    />
                </div>
                {fullContactLink && <ContactBanner contactLink={fullContactLink} />}
                <CheckBrowserContainer userAgent={window.navigator.userAgent} />
                <div className="main-content">
                    <Suspense fallback={<div />}>
                        <Routes>
                            <Route element={getBreadcrumbLayout()} path="*" />
                        </Routes>
                    </Suspense>
                </div>
            </div>
            <CookiesModal isOpen={isCookiesModalOpened} onClose={onCookiesClose} />
            <Footer onCookiesClick={onCookiesClick} onLegalClick={onLegalClick} onRgpdClick={onRgpdClick} />
        </div>
    )
}

MainLayout.propsTypes = {
    actualPage: PropTypes.string.isRequired,
    getParam: PropTypes.func.isRequired,
    isCookiesModalOpened: PropTypes.bool.isRequired,
    isMenuResponsive: PropTypes.bool.isRequired,
    onConnectClick: PropTypes.func.isRequired,
    onCookiesClick: PropTypes.func.isRequired,
    onCookiesClose: PropTypes.func.isRequired,
    onDisconnectClick: PropTypes.func.isRequired,
    onHomeClick: PropTypes.func.isRequired,
    onLegalClick: PropTypes.func.isRequired,
    onProfileClick: PropTypes.func.isRequired,
    onRgpdClick: PropTypes.func.isRequired,
}

const mapStateToProps = state => {
    let actualPage = getActualPage(state)
    return {
        actualPage: actualPage,
        isCookiesModalOpened: showCookiesModal(state),
        isMenuResponsive: isMenuResponsive(state),
        isUserConnected: isUserConnected(state) && actualPage !== Page.BYPASS_LOGIN,
        userInfo: getUserInfo(state),
        userPermissions: getUserPermissions(state),
        username: getUserFullname(state),
    }
}

const mapDispatchToProps = (dispatch, props) => ({
    // Action lors de la fermeture de la modale des cookies
    onCookiesClose: () =>
        dispatch(
            saveCookiesChoices({
                isModalOpened: false,
                expirationDate: dayjs().add(6, "month"),
                enabled: true,
            }),
        ),

    // Display cookies modal
    onCookiesClick: () => dispatch(saveCookiesChoices({isModalOpened: true, step: "CONFIGURATION"})),

    // Go to the connection page.
    onConnectClick: () => props.router.navigate(PagesArchitecture[Page.LOGIN].route),

    // Lien vers la documentation RGPD
    onRgpdClick: () => window.open(DOMISERVE_DONNEES_PERSONNELLES_URL),

    // Disconnect the current user.
    disconnectUser: () => dispatch(disconnectUser()),

    // Get parameter from DB
    getParam: paramId => dispatch(getParameter(paramId)),

    logoutUser: () => dispatch(logoutUser()),

    // Go to the home page.
    onHomeClick: () => {
        props.router.navigate(PagesArchitecture[Page.HOME].route)
    },

    //Go to user profile page
    onProfileClick: () => props.router.navigate(PagesArchitecture[Page.USER_PROFILE_MAIN_INFORMATIONS].route),

    // Go to the legal mentions page.
    onLegalClick: () => props.router.navigate(PagesArchitecture[Page.LEGAL_NOTICE].route),

    setMenuResponsive: isResponsive => {
        dispatch(setResponsive(isResponsive))
    },
})

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(MainLayout))
